<template>
  <div class="viewer-layout">
    <div class="top-bar">
      <div class="top-bar-content">
        <div class="page-title">{{ pageTitle }}</div>
      </div>
    </div>
    <div v-if="!isCollapse && isMobile" class="sidebar-mask" @click="toggleSidebar"></div>
    <el-container>
      <el-aside 
        class="sidebar-container" 
        :style="sidebarStyle">
        <div class="sidebar-header">
          <div class="logo-wrapper">
            <div class="logo">题库</div>
          </div>
          <div class="title-wrapper">
            <div class="school-title">题库中心</div>
            <div class="module-title">使用人员</div>
          </div>
        </div>
        
        <div class="sidebar-nav">
          <div class="nav-group">
            <div class="nav-group-title">功能导航</div>
            <el-menu
              :default-active="activeMenu"
              class="sidebar-menu"
              router
              :collapse="isCollapse"
              background-color="#fff"
              text-color="#303133"
              active-text-color="#4f46e5"
              @select="handleMenuSelect">
              <el-menu-item :index="getRoutePath('dashboard')">
                <i class="el-icon-collection"></i>
                <span slot="title">题库系统</span>
              </el-menu-item>
              <el-menu-item :index="getRoutePath('favorites')">
                <i class="el-icon-star-on"></i>
                <span slot="title">我的收藏</span>
              </el-menu-item>
            </el-menu>
          </div>
        </div>
      </el-aside>
      
      <el-container class="main-container" :style="mainContainerStyle">
        <el-header height="60px" class="header">
          <div class="header-left">
            <i 
              :class="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'" 
              class="collapse-btn"
              @click="toggleSidebar"></i>
          </div>
          <div class="header-right">
            <el-dropdown trigger="click" @command="handleCommand">
              <div class="avatar-container">
                <el-avatar :size="32" :src="userAvatar" icon="el-icon-user-solid"></el-avatar>
                <span class="name">{{ userName }}</span>
                <i class="el-icon-caret-bottom"></i>
              </div>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="profile">个人信息</el-dropdown-item>
                <el-dropdown-item command="phone">更新手机号</el-dropdown-item>
                <el-dropdown-item command="password">修改密码</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-header>
        
        <el-main class="main-content">
          <router-view />
        </el-main>
        
        <el-footer height="40px" class="footer">
          <div class="footer-content">
            <span>© {{ currentYear }} 题库系统</span>
          </div>
        </el-footer>
      </el-container>
    </el-container>
    
    <!-- 个人信息对话框 -->
    <el-dialog title="个人信息" :visible.sync="profileDialogVisible" width="500px">
      <div v-if="userProfile" class="profile-container">
        <div class="profile-header">
          <el-avatar :size="80" :src="userProfile.avatar" icon="el-icon-user-solid"></el-avatar>
          <div class="profile-name">{{ userProfile.username }}</div>
        </div>
        
        <el-divider></el-divider>
        
        <div class="profile-info">
          <div class="info-item">
            <span class="info-label">手机号码：</span>
            <span class="info-value">{{ userProfile.phone || '未设置' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">性别：</span>
            <span class="info-value">{{ formatGender(userProfile.gender) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">角色：</span>
            <span class="info-value">查看人员</span>
          </div>
          <div class="info-item">
            <span class="info-label">状态：</span>
            <span class="info-value">
              <el-tag :type="userProfile.status === 1 ? 'success' : 'danger'" size="mini">
                {{ userProfile.status === 1 ? '正常' : '禁用' }}
              </el-tag>
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">最后登录：</span>
            <span class="info-value">{{ formatDate(userProfile.last_login_time) }}</span>
          </div>
        </div>
        
        <el-divider>校区信息</el-divider>
        
        <div v-if="userProfile.campus_info && userProfile.campus_info.length > 0" class="campus-list">
          <div v-for="(campus, index) in userProfile.campus_info" :key="index" class="campus-item">
            <div class="campus-name">
              <span>{{ campus.campus_name }}</span>
              <el-tag v-if="campus.is_main === 1" size="mini" type="primary">主校区</el-tag>
            </div>
            <div class="campus-detail">
              <div class="detail-item">
                <i class="el-icon-location"></i>
                <span>{{ campus.address }}</span>
              </div>
              <div class="detail-item">
                <i class="el-icon-phone"></i>
                <span>{{ campus.contact_phone }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="no-campus">暂无校区信息</div>
      </div>
      
      <div v-else class="profile-loading">
        <el-skeleton :rows="6" animated />
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="profileDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
    
    <!-- 引入修改密码组件 -->
    <password-dialog v-model="passwordDialogVisible" :user-profile="userProfile" @password-changed="handlePasswordChanged"></password-dialog>
    
    <!-- 引入更新手机号组件 -->
    <phone-dialog v-model="phoneDialogVisible" :user-profile="userProfile" @phone-updated="handlePhoneUpdated"></phone-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import PasswordDialog from '@/components/PasswordDialog.vue';
import PhoneDialog from '@/components/PhoneDialog.vue';

export default {
  name: 'QuestionViewerLayout',
  components: {
    PasswordDialog,
    PhoneDialog
  },
  data() {
    return {
      isMobile: false,
      profileDialogVisible: false,
      passwordDialogVisible: false,
      phoneDialogVisible: false,
      userProfile: null,
      campusCode: null
    };
  },
  computed: {
    ...mapGetters({
      isCollapse: 'isCollapse',
      userName: 'auth/userName',
      userAvatar: 'auth/userAvatar'
    }),
    activeMenu() {
      return this.$route.path;
    },
    currentYear() {
      return new Date().getFullYear();
    },
    pageTitle() {
      const route = this.$route;
      return route.meta && route.meta.title ? route.meta.title : '题库系统';
    },
    sidebarStyle() {
      if (this.isMobile) {
        return {
          transform: this.isCollapse ? 'translateX(-210px)' : 'translateX(0)'
        };
      }
      return {
        transform: 'translateX(0)'
      };
    },
    mainContainerStyle() {
      if (this.isMobile) {
        return {
          marginLeft: '0'
        };
      }
      return {
        marginLeft: '210px'
      };
    }
  },
  methods: {
    ...mapActions(['toggleSidebar']),
    handleCommand(command) {
      switch (command) {
        case 'profile':
          this.showProfile();
          break;
        case 'phone':
          this.showPhoneDialog();
          break;
        case 'password':
          this.showPasswordDialog();
          break;
        case 'logout':
          this.logout();
          break;
      }
    },
    
    // 显示手机更新对话框
    showPhoneDialog() {
      if (!this.userProfile) {
        this.showProfile().then(() => {
          this.phoneDialogVisible = true;
        });
      } else {
        this.phoneDialogVisible = true;
      }
    },
    
    // 处理手机号更新成功
    handlePhoneUpdated(newPhone) {
      if (this.userProfile) {
        this.userProfile.phone = newPhone;
      }
      this.$message.success('手机号已更新为：' + newPhone);
      
      setTimeout(() => {
        this.$message.info('手机号已更新，请重新登录');
        this.logout();
      }, 1500);
    },
    
    // 显示密码修改对话框
    showPasswordDialog() {
      if (!this.userProfile) {
        this.showProfile().then(() => {
          this.passwordDialogVisible = true;
        });
      } else {
        this.passwordDialogVisible = true;
      }
    },
    
    // 处理密码修改成功
    handlePasswordChanged() {
      setTimeout(() => {
        this.logout();
      }, 1500);
    },
    
    async showProfile() {
      this.profileDialogVisible = true;
      this.userProfile = null;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/v1/profile/info', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.code === 200) {
          this.userProfile = response.data.data;
          console.log('个人资料:', this.userProfile);
        } else {
          this.$message.error('获取个人信息失败：' + (response.data.msg || '未知错误'));
        }
      } catch (error) {
        console.error('获取个人信息失败:', error);
        this.$message.error('获取个人信息失败，请稍后重试');
      }
    },
    
    formatGender(gender) {
      const genderMap = {
        0: '未知',
        1: '男',
        2: '女'
      };
      return genderMap[gender] || '未知';
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '未登录';
      
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      } catch (error) {
        return dateStr;
      }
    },
    
    logout() {
      const schoolCode = this.$route.params.schoolCode || localStorage.getItem('schoolCode') || '';
      
      this.$store.dispatch('auth/logout').then(() => {
        if (schoolCode && schoolCode !== 'undefined') {
          this.$router.push(`/${schoolCode}/login`);
        } else {
          this.$router.push('/login');
        }
      });
    },
    
    getRoutePath(path) {
      const schoolCode = this.$route.params.schoolCode || localStorage.getItem('schoolCode') || '';
      const campusCode = this.getCampusCode();
      
      if (schoolCode && schoolCode !== 'undefined') {
        if (campusCode && campusCode !== 'undefined') {
          // 如果有校区代码，则使用 /schoolCode/campusCode/question-viewer/path 格式
          return path ? `/${schoolCode}/${campusCode}/question-viewer/${path}` : `/${schoolCode}/${campusCode}/question-viewer`;
        } else {
          // 如果没有校区代码，则使用 /schoolCode/question-viewer/path 格式
          return path ? `/${schoolCode}/question-viewer/${path}` : `/${schoolCode}/question-viewer`;
        }
      }
      
      // 如果都没有，则使用基础路径
      return path ? `/question-viewer/${path}` : `/question-viewer`;
    },
    
    getCampusCode() {
      // 获取校区代码，首先从路由参数获取，如果没有则从本地存储获取
      let campusCode = this.$route.params.campusCode;
      if (!campusCode) {
        const campusInfo = localStorage.getItem('currentCampus');
        if (campusInfo) {
          try {
            const parsedInfo = JSON.parse(campusInfo);
            campusCode = parsedInfo.code;
          } catch (e) {
            console.error('解析校区信息失败:', e);
          }
        }
      }
      
      if (!campusCode) {
        campusCode = localStorage.getItem('campusCode');
      }
      
      return campusCode;
    },
    
    checkScreenSize() {
      const oldIsMobile = this.isMobile;
      this.isMobile = window.innerWidth < 768;
      
      if (oldIsMobile !== this.isMobile) {
        this.$store.dispatch('toggleSidebar', this.isMobile);
      }
    },
    
    handleMenuSelect() {
      if (this.isMobile && !this.isCollapse) {
        this.$store.dispatch('toggleSidebar', true);
      }
    }
  },
  created() {
    // 从路由参数或本地存储获取校区代码
    const campusCode = this.$route.params.campusCode || localStorage.getItem('campusCode');
    this.campusCode = campusCode;
    console.log('当前校区代码:', campusCode);
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
    
    if (this.isMobile) {
      if (!this.isCollapse) {
        this.$store.dispatch('toggleSidebar', true);
      }
    } else {
      if (this.isCollapse) {
        this.$store.dispatch('toggleSidebar', false);
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  watch: {
    '$route'(to, from) {
      console.log('路由变化:', from.path, '->', to.path);
      
      if (this.isMobile && !this.isCollapse) {
        this.$store.dispatch('toggleSidebar', true);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.viewer-layout {
  height: 100vh;
  width: 100%;
  overflow: auto;
  
  .top-bar {
    height: 40px;
    background: linear-gradient(to right, #4f46e5, #7e22ce);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    
    .top-bar-content {
      height: 100%;
      display: flex;
      align-items: center;
      padding: 0 20px;
      
      .page-title {
        color: white;
        font-size: 16px;
        font-weight: 500;
      }
    }
  }
  
  .sidebar-mask {
    position: fixed;
    top: 40px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
  }
   
  .sidebar-container {
    width: 210px !important;
    background-color: #fff;
    height: calc(100vh - 40px);
    position: fixed;
    top: 40px;
    left: 0;
    z-index: 999;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
    overflow-y: auto;
    
    .sidebar-header {
      height: 60px;
      display: flex;
      align-items: center;
      padding: 0 15px;
      border-bottom: 1px solid #f0f0f0;
      
      .logo-wrapper {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #4f46e5;
        color: white;
        border-radius: 4px;
        margin-right: 10px;
        
        .logo {
          font-size: 16px;
          font-weight: bold;
        }
      }
      
      .title-wrapper {
        display: flex;
        flex-direction: column;
        
        .school-title {
          font-size: 16px;
          font-weight: 500;
          color: #303133;
        }
        
        .module-title {
          font-size: 12px;
          color: #606266;
        }
      }
    }
    
    .sidebar-nav {
      padding: 15px 0;
      
      .nav-group {
        margin-bottom: 20px;
        
        .nav-group-title {
          font-size: 12px;
          color: #909399;
          padding: 0 15px;
          margin-bottom: 10px;
        }
        
        .sidebar-menu {
          border-right: none;
          
          .el-menu-item {
            height: 50px;
            line-height: 50px;
            
            &.is-active {
              background-color: #f0f2ff;
              border-right: 3px solid #4f46e5;
              
              i {
                color: #4f46e5;
              }
            }
            
            i {
              margin-right: 5px;
              font-size: 18px;
              color: #606266;
            }
          }
        }
      }
    }
  }
  
  .main-container {
    min-height: 100vh;
    margin-top: 40px;
    margin-left: 210px;
    transition: margin-left 0.3s ease;
    
    .header {
      background-color: #fff;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 20px;
      
      .header-left {
        .collapse-btn {
          font-size: 20px;
          cursor: pointer;
          color: #606266;
          
          &:hover {
            color: #409EFF;
          }
        }
      }
      
      .header-right {
        .avatar-container {
          display: flex;
          align-items: center;
          cursor: pointer;
          
          .name {
            margin: 0 5px;
            color: #303133;
          }
        }
      }
    }
    
    .main-content {
      padding: 20px;
      background-color: #f5f7fa;
      min-height: calc(100vh - 140px);
    }
    
    .footer {
      background-color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .footer-content {
        color: #909399;
        font-size: 12px;
      }
    }
  }
  
  .profile-container {
    .profile-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 20px;
      
      .profile-name {
        margin-top: 10px;
        font-size: 18px;
        font-weight: 500;
      }
    }
    
    .profile-info {
      .info-item {
        display: flex;
        margin-bottom: 15px;
        
        .info-label {
          width: 100px;
          text-align: right;
          color: #606266;
          margin-right: 10px;
        }
        
        .info-value {
          flex: 1;
        }
      }
    }
    
    .campus-list {
      .campus-item {
        margin-bottom: 15px;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        padding: 10px;
        
        .campus-name {
          font-weight: 500;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          
          span {
            margin-right: 10px;
          }
        }
        
        .campus-detail {
          color: #606266;
          font-size: 13px;
          
          .detail-item {
            display: flex;
            align-items: center;
            margin-bottom: 5px;
            
            i {
              margin-right: 5px;
              color: #909399;
            }
          }
        }
      }
    }
    
    .no-campus {
      text-align: center;
      color: #909399;
      font-style: italic;
      padding: 20px 0;
    }
  }
  
  .profile-loading {
    padding: 20px 0;
  }
}

@media screen and (max-width: 767px) {
  .viewer-layout {
    .main-container {
      margin-left: 0 !important;
    }
  }
}
</style> 